/**
 * 🥶 What the weather costs you — **T131, D64, D85**.
 *
 * 🔴 **This is the file that makes the weather stop being scenery.** The game
 * has had real archived Icelandic weather since **D57** and it has never once
 * mattered. Now it does: **exposure is a multiplier on the food drain**, and
 * nothing else.
 *
 * ## The whole idea, in haugur's sentence
 *
 * > *"if you have to stay out in a storm and cold conditions, you can survive
 * > but you better have food in your inventory to keep that health up. **you
 * > work at same speed but no food you die.** That's true survival and every
 * > icelander knows it."*
 *
 * So exposure **never slows you, never blocks you, never takes the controls**
 * (**D24** — the family are builders). It makes the clock take food faster.
 * ✅ **No new meter, no new bar, no new screen** — one currency for everything,
 * which is [ADR-0016](../../../Project/adr/0016-cold-is-charged-to-the-food-meter.md)'s
 * whole argument.
 *
 * ## 🔴 A handful of yes/no checks, NEVER a temperature field
 *
 * **D64 §6 bans an ambient temperature value**, and the reason is the one that
 * decides everything here: *its bugs would look like weather bugs*, which on
 * this project means nobody would ever find them (**D2**). So this file asks
 * five small questions with small answers — *is it cold · is there wind · are
 * you wet · where are you standing · what are you wearing* — and adds up what
 * they cost.
 *
 * ⚠️ **The gauge is snapped for the same reason.** A smooth comfort value is a
 * temperature field wearing a different name (**D84**).
 *
 * ## 🌧️ Wet COSTS SOMETHING SINCE 2026-09-01 — D101
 *
 * 🎯 **haugur, 2026-08-21, while this was being designed:** *"we have not
 * designed rain or snow yet, yet those have a say in it when you get cold."*
 *
 * 🔴 **He was right, and it would have been invisible.** The archive knew it was
 * raining and the top bar even said *rigning* — but **no rain was drawn falling**,
 * so a player made cold by wet had no way to see why. **That is precisely the
 * fault §6 exists to prevent.** ✅ **So `wetSurcharge` was `0` for eleven days on
 * purpose**, built, typed and tested and costing nothing — **a number in a data
 * file, not a branch in the code.**
 *
 * ✅ **RAIN IS DRAWN NOW** (`client/src/rain.ts`, T155), so the reason is gone and
 * **`wetSurcharge` is `0.35` — half of wind, and the same in July as in January**,
 * which is what makes +4 °C and pouring dangerous for the first time. 🔴 **The
 * worst case moved from about 25 real minutes to about 22, and haugur chose that
 * knowingly**, priced in minutes rather than in numbers. ⚠️ **Snow still lies
 * nowhere** (T156); wet is charged from the archive's rain alone.
 *
 * 👉 **`tuning.json`'s `$whyWetCostsSOMETHINGATLAST` is the long version** and it
 * is the one that gets maintained.
 */

import { TUNING, type Tuning } from './tuning.ts';
import type { Weather } from './weather.ts';
import { underARoof, type ShelterRung } from './shelter.ts';

/* ── the three things the weather can do to you ───────────────────────────── */

/**
 * **D64 §4: cold, wind and wet.** All three are already on the wire — the
 * archived Veðurstofa readings, per zone, per player (**D57**, **D58**).
 *
 * ⚠️ **The order is worst-first and it is the order the screen reads them in.**
 */
export const EXPOSURE_FACTORS = ['cold', 'wind', 'wet'] as const;

export type ExposureFactor = (typeof EXPOSURE_FACTORS)[number];

export interface Exposure {
  /**
   * What the clock's food drain is multiplied by. **1 means nothing is biting.**
   *
   * ⚠️ **A multiplier on rule ① of `vitals.ts`, not a fourth rule.** That is
   * ADR-0016's §2 exactly: exposure is *a condition, like standing in fire*.
   */
  readonly foodDrainMultiplier: number;
  /**
   * Which factors are actually costing you something, worst first.
   *
   * ⚠️ **Only factors that COST something appear here**, so a factor tuned to
   * zero is silent rather than mentioned-but-free. This is what the HUD names
   * (**D64 §7**).
   */
  readonly biting: readonly ExposureFactor[];
  /**
   * The HUD's exposure gauge: **−1 (worst) … 0 (comfortable)**.
   *
   * 🔴 **Snapped to {@link GAUGE_STEPS} positions, never smooth** — **D84**.
   * ⚠️ **The warm half of the gauge is drawn and unreachable**: nothing in
   * Iceland makes you too hot, so this never returns a positive number.
   */
  readonly gauge: number;
}

/** Nothing is biting. What a Blíða player gets, without an exemption (**D83**). */
export const NO_EXPOSURE: Exposure = { foodDrainMultiplier: 1, biting: [], gauge: 0 };

/**
 * How many positions the gauge may take between comfortable and worst.
 *
 * ⚠️ **Four steps, so five positions including zero.** Enough that the needle
 * moves when something changes, few enough that it is obviously a ladder rather
 * than a thermometer.
 */
export const GAUGE_STEPS = 4;

export interface ExposureInputs {
  /** Where you are standing — T132's answer. */
  readonly rung: ShelterRung;
  /** The archived reading where you are standing, or `null` if none has arrived. */
  readonly weather: Weather | null;
  /**
   * 👕 How much of the surcharge your clothes take off, **0 … below 1**.
   *
   * 🎯 **D64 §5, haugur explicitly: clothing REDUCES, it does not cancel.**
   * *"Wool buys you an hour" is the honest version of Iceland; "wool solves
   * winter" is not.* ⚠️ **A deliberate departure from Minecraft**, where any
   * single piece of leather switches freezing off outright — and from
   * `Minecraft/05_Survival_Systems.md` §9.5, which **recommended the full
   * cancel** on simplicity grounds. **haugur overruled it knowingly.**
   *
   * ⚠️ **Nothing can wear anything yet.** Four slots exist and no wool does, so
   * this is `0` in the game and non-zero only in the tests — the same
   * built-but-unreachable pattern as the hearth.
   */
  readonly clothingReduction?: number;
}

/* ── the answer ───────────────────────────────────────────────────────────── */

/**
 * 🥶 What the weather is costing this player, right now.
 *
 * ⚠️ **Pure.** The server calls it on its save interval to charge the drain, and
 * the client calls it to draw the gauge — the same arrangement `stepVitals` and
 * `shelterAt` already have, and the reason the number on screen cannot disagree
 * with the number in the database.
 */
export function exposureFor(inputs: ExposureInputs, tuning: Tuning = TUNING): Exposure {
  const { rung, weather } = inputs;
  const t = tuning.exposure;

  /* 🔥 The top rung: a fire indoors turns exposure off outright (D64 §3).
     ✅ REACHABLE IN THE GAME since 2026-08-31 — light a hearth in a room and
     this is what stops the cold. It was built unreachable on 2026-08-25 and the
     prediction held: switching it on was an answer to `fireAt`, and not one
     line of this file changed. */
  if (rung === 'hearth') return NO_EXPOSURE;

  /* ⚠️ No reading means no claim. A player is not charged for weather we do not
     have — inventing a temperature from a gap in the archive would be exactly
     the bug §6 says nobody would ever find. */
  if (weather === null) return NO_EXPOSURE;

  /* ☂️ One shared answer — see `underARoof`. ⚠️ Behaviour is unchanged: `hearth`
     returned above, so this used to read `rung === 'enclosed'` and meant the
     same thing. Naming it is what stops the rain writing a second copy. */
  const indoors = underARoof(rung);
  const windBlocked = indoors || rung === 'lee';

  /* ① What the sky is doing, before any shelter is taken into account. */
  const cold = weather.temperature !== null && weather.temperature < t.coldBelowCelsius;
  const windy = weather.wind !== null && weather.wind >= t.windAboveMetresPerSecond;
  const wet = weather.rain !== null && weather.rain > 0;

  /* ② What your shelter takes away. D64 §3's ladder, and the rung that separates
     wind from cold is the one that matters here — a lee or a cave stops the wind
     and does nothing about the temperature, which is what actually happens. */
  /* ⚠️ A Map rather than an object, for the same reason `blocks.ts` uses one:
     `surcharges[factor]` is the pattern `security/detect-object-injection`
     flags, and this project ANSWERS that rule rather than suppressing it. */
  const surcharges = new Map<ExposureFactor, number>(
    Object.entries({
      /* Indoors removes MOST of the cold, not all of it — a turf house without a
       fire in it is cold, and D64 §3 gives the full stop to the fire alone. */
      cold: cold ? t.coldSurcharge * (indoors ? t.indoorsColdShare : 1) : 0,
      wind: windy && !windBlocked ? t.windSurcharge : 0,
      /* A roof keeps the rain off; the lee of a wall does not. */
      wet: wet && !indoors ? t.wetSurcharge : 0,
    }) as [ExposureFactor, number][],
  );

  /* ③ Clothing takes a share off the whole surcharge and can never take all of
     it — the clamp is the mechanism behind "reduces, does not cancel". */
  const reduction = clamp(inputs.clothingReduction ?? 0, 0, t.clothingReductionCeiling);

  let surcharge = 0;
  const biting: ExposureFactor[] = [];
  for (const factor of EXPOSURE_FACTORS) {
    const cost = (surcharges.get(factor) ?? 0) * (1 - reduction);
    if (cost <= 0) continue;
    surcharge += cost;
    biting.push(factor);
  }

  return {
    foodDrainMultiplier: 1 + surcharge,
    biting,
    gauge: snapGauge(surcharge, worstSurcharge(t)),
  };
}

/**
 * The gauge, as one of a few fixed positions.
 *
 * 🔴 **Snapped, and that is D84 rather than a rendering choice.** A needle that
 * glides is reporting a continuous comfort value, which is the temperature field
 * ADR-0016 §6 bans wearing a different name. **Five positions, and you can say
 * which one is wrong.**
 */
export function snapGauge(surcharge: number, worst: number): number {
  if (surcharge <= 0 || worst <= 0) return 0;
  const share = Math.min(1, surcharge / worst);
  return -Math.round(share * GAUGE_STEPS) / GAUGE_STEPS;
}

/**
 * The worst the weather can cost, on open ground with nothing worn.
 *
 * ⚠️ **Derived, never written down twice.** It is what the gauge's −1 means, and
 * a second constant saying "the worst is 2.4" would be the two-numbers-drift
 * failure this project keeps finding.
 */
export function worstSurcharge(t: Tuning['exposure']): number {
  return t.coldSurcharge + t.windSurcharge + t.wetSurcharge;
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, value));
}
